import { Container, Divider } from "@material-ui/core";
import { Box, Grid, Typography } from "@mui/material";
import axios from "axios";
import {
  CurrencyDisplay,
  CustomTable,
  FormCard,
  ModalError,
  ModalSuccess,
  ModalWarning,
  Page,
  PrimaryButton,
  SearchTextInput,
  SectionLabel,
  SkeletonComponent,
  StatusChip,
  SwitchInput,
  TableAction,
  TableCellMultipleRow,
} from "components";
import { InnoImage } from "inno-ui";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { Fragment, useEffect, useRef, useState } from "react";
import { useHistory, useLocation, useParams } from "react-router";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { downloadFileFromRes, formatDate, getErrors } from "utils";
import { ModalDetailRoyaltyTracking } from "../Components/Modal";
import RoyaltyTrackingSummarytCard from "../Components/Card";

const DetailRoyaltyTracking = ({ userLogin }) => {
  const classes = globalStyles();
  const { state } = useLocation();
  const { id } = useParams();
  const inputRef = useRef(null);
  const history = useHistory();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const [loadingPage, setLoadingPage] = useState(false);

  const userPublisher = userLogin?.publisher;
  const [preload, setPreload] = useState();
  const [modalVisible, setModalVisible] = useState(false);
  const [loadingButton, setLoadingButton] = useState(false);
  const [pageDetail, setPageDetail] = useState([]);
  const [showDetail, setShowDetail] = useState(false);
  const [loadingTable, setLoadingTable] = useState(true);
  const [dataTable, setDataTable] = useState([]);
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
    search: "",
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);

  const handleDownloadXLSX = async () => {
    try {
      const res = await axios.get(
        `${hardBaseUrl}/publisher/royalty-tracking/v1/sales-invoice/export/${id}`,
        {
          headers,
          responseType: "blob",
        }
      );
      downloadFileFromRes(res);
    } catch (error) {
      ModalError(getErrors(error?.response));
    }
  };

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const handleDownloadPdf = async () => {
    const doc = new jsPDF("p", "mm", "a4");

    const fontNormal = 9;

    const headerBottom = Math.ceil(Math.max(14, 96, 80));

    // Fungsi header biar bisa dipakai ulang
    const drawHeader = doc => {
      // Logo
      if (userPublisher?.profile_image) {
        try {
          doc.addImage(userPublisher.profile_image, "PNG", 8, 4, 25, 15);
        } catch (err) {
          console.warn("addImage gagal:", err);
        }
      }

      const lastY = 16;
      doc.setDrawColor(200);
      doc.line(8, lastY + 10, 200, lastY + 10);

      // Invoice Info
      doc.setTextColor("#263238");
      doc.setFontSize(fontNormal);

      doc.setFont("helvetica", "bold");
      doc.text("Client", 14, 40);
      doc.setFont("helvetica", "normal");
      doc.text(pageDetail.client_name || "-", 14, 45);

      doc.setFont("helvetica", "bold");
      doc.text("Type", 14, 54);
      doc.setFont("helvetica", "normal");
      doc.text(pageDetail.invoice_type, 14, 59);

      doc.setFont("helvetica", "bold");
      doc.text("Invoice Number", 14, 66);
      doc.setFont("helvetica", "normal");
      doc.text(pageDetail.invoice_number, 14, 71);

      doc.setFont("helvetica", "bold");
      doc.text("Invoice Date", 14, 78);
      doc.setFont("helvetica", "normal");
      doc.text(formatDate(pageDetail.invoice_date), 14, 83);

      doc.setFont("helvetica", "bold");
      doc.text("Period", 130, 42);
      doc.setFont("helvetica", "normal");
      doc.text(pageDetail.period, 130, 47);

      doc.setFont("helvetica", "bold");
      doc.text("Total Composer/Author", 130, 54);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Rp${Number(pageDetail.total_composer_revenue || 0).toLocaleString(
          "id-ID"
        )}` || "-",
        130,
        59
      );

      doc.setFont("helvetica", "bold");
      doc.text("Total Paid", 130, 66);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Rp${Number(pageDetail.total_paid || 0).toLocaleString("id-ID")}`,
        130,
        71
      );

      doc.setFont("helvetica", "bold");
      doc.text("Total Unpaid", 130, 78);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Rp${Number(pageDetail.total_unpaid || 0).toLocaleString("id-ID")}`,
        130,
        83
      );
    };

    let finalY = headerBottom;

    const data = dataTable?.map(song => {
      return columnTableDetail(pageDetail)?.map(col => {
        switch (col.headerAlign) {
          case "right":
            return `Rp${Number(song?.[col.name] || 0).toLocaleString("id-ID")}`;

          default:
            return song?.[col.name] || "-";
        }
      });
    });

    autoTable(doc, {
      head: [columnTableDetail(pageDetail)?.map(col => col.title)],
      body: data,
      startY: headerBottom,
      margin: { top: headerBottom },
      styles: { fontSize: 8, cellPadding: 3, valign: "middle" },
      headStyles: {
        fillColor: [230, 230, 230],
        textColor: 20,
        fontStyle: "bold",
      },
      didDrawPage: dataArg => {
        drawHeader(doc, dataArg.pageNumber);
      },
    });

    finalY = doc.lastAutoTable.finalY || headerBottom;
    finalY += 10;

    // =========================================================
    // PENOMORAN HALAMAN AKHIR (Mencegah Bentrokan Footer)
    // =========================================================
    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFont("helvetica", "normal");
      doc.setFontSize(8);
      doc.setTextColor("#6F6F84");

      // Posisikan nomor halaman 4mm dari bawah (di luar area Total Fee)
      const pageY = doc.internal.pageSize.getHeight() - 4;

      doc.text(
        `Page ${i} of ${pageCount}`,
        doc.internal.pageSize.getWidth() - 8,
        pageY,
        { align: "right" }
      );
    }

    doc.setPage(pageCount);
    doc.save(`${pageDetail.invoice_number}.pdf`);
  };

  const getDataDetail = async () => {
    try {
      setLoadingTable(true);
      const res = await axios.get(
        `${hardBaseUrl}/publisher/royalty-tracking/v1/sales-invoice/${id}`,
        { headers }
      );
      const resTable = await axios.get(
        `${hardBaseUrl}/publisher/royalty-tracking/v1/sales-invoice/${id}/composers`,
        { headers, ...{ params: queryParams } }
      );
      const data = res?.data?.data;
      const dataTable = resTable?.data?.data;
      setPageDetail(data || {});
      setDataTable(dataTable || []);
      const pageCount = getTotalPage(
        resTable?.data?.meta?.total,
        queryParams?.size
      );
      setTableTotalPage(pageCount || 1);
    } catch (error) {
      ModalError(error?.response ? getErrors(error?.response) : error);
    } finally {
      setLoadingTable(false);
    }
  };

  useEffect(() => {
    getDataDetail();
  }, [queryParams]);

  return (
    <Page className={classes.root} title="Royalty Tracking Detail">
      {loadingPage ? (
        <SkeletonComponent variant="wave" />
      ) : (
        <Container maxWidth={false}>
          <HeaderTitle
            title={pageDetail?.invoice_number}
            breadcrumbData={breadcrumbData}
            backButton
          />
          <Grid container>
            <Grid item xs={12}>
              <FormCard ref={inputRef}>
                <Grid container mb={2}>
                  <Grid
                    item
                    xs={12}
                    sm={8}
                    spacing={2}
                    display={"flex"}
                    flexWrap={"wrap"}
                  >
                    {formInvoiceDetail?.map(
                      ({ label, value, blank }, index) => (
                        <Grid item xs={12} sm={6} key={index} mb={4}>
                          {!blank && (
                            <Fragment>
                              <Typography>{`${label}:`}</Typography>
                              <SectionLabel
                                fontSize="14px"
                                fontSubTitleSize="12px"
                                title={pageDetail?.[value] || "-"}
                              />
                            </Fragment>
                          )}
                        </Grid>
                      )
                    )}
                  </Grid>
                  <Grid
                    item
                    xs={12}
                    sm={4}
                    display={"flex"}
                    gap={2}
                    justifyContent={"flex-end"}
                  >
                    <Grid item>
                      <PrimaryButton
                        size="large"
                        label="Download PDF"
                        onClick={handleDownloadPdf}
                      />
                    </Grid>
                    <Grid item>
                      <PrimaryButton
                        size="large"
                        label="Download XLXS"
                        onClick={handleDownloadXLSX}
                      />
                    </Grid>
                  </Grid>
                </Grid>
                <Grid
                  container
                  justifyContent="space-between"
                  alignItems="center"
                  my={2}
                  rowSpacing={1}
                >
                  <Grid>
                    <SearchTextInput
                      placeholder="Search"
                      value={queryParams?.search}
                      onChange={e =>
                        handleChangeQueryParams(e?.target?.value, "search")
                      }
                    />
                  </Grid>
                  <Grid width={680}>
                    <RoyaltyTrackingSummarytCard chartReport={pageDetail} />
                  </Grid>
                </Grid>
                <Divider className={classes?.divider} />
                <CustomTable
                  isLoading={loadingTable}
                  data={dataTable}
                  columnTable={columnTableDetail(pageDetail)}
                  page={queryParams?.page}
                  pageSize={queryParams?.size}
                  pageCount={tableTotalPage}
                  handleChangePage={(_, page) =>
                    handleChangeQueryParams(page, "page")
                  }
                  handleChangePageSize={e =>
                    handleChangeQueryParams(e?.target?.value, "size")
                  }
                  my="16px"
                  isHaveAction
                  renderAction={item => {
                    return (
                      <TableAction
                        tooltipView="View"
                        handleView={() => {
                          setPreload({
                            composer_id: item.composer_id,
                            composer_name: item.composer_name,
                            sales_invoice_id: id,
                          });
                          setModalVisible(true);
                        }}
                      />
                    );
                  }}
                />
              </FormCard>
            </Grid>
          </Grid>
        </Container>
      )}
      {modalVisible && (
        <ModalDetailRoyaltyTracking
          open={modalVisible}
          preload={preload}
          onClose={() => setModalVisible(false)}
        />
      )}
    </Page>
  );
};

const breadcrumbData = [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Royalty Tracking",
    link: "/admin/Royalty Tracking",
  },
  {
    label: "Detail Royalty Tracking",
    active: true,
  },
];
export const optionsInvoiceStatus = [
  {
    name: "Draft",
    value: "draft",
    color: "warning",
  },
  {
    name: "Waiting for Approval",
    value: "waiting_for_approval",
    color: "warning",
  },
  {
    name: "Approved",
    value: "approved",
    color: "success",
  },
  {
    name: "Paid Updating Dashboard",
    value: "paid_updating_dashboard",
    color: "warning",
  },
  {
    name: "Paid",
    value: "paid",
    color: "success",
  },
  {
    name: "Rejected",
    value: "rejected",
    color: "danger",
  },
];
const columnTableDetail = pageDetail => [
  {
    name: "composer_name",
    title: "Composer/Author",
  },

  {
    name: "total_songs",
    title: "Songs",
  },
  {
    name: "revenue",
    title: "Total Revenue",
    headerAlign: "right",
    renderText: revenue => (
      <CurrencyDisplay value={revenue} suffix=",-" prefix={"Rp"} />
    ),
  },
  {
    name: "paid",
    title: "Paid",
    headerAlign: "right",
    renderText: paid => (
      <CurrencyDisplay value={paid} suffix=",-" prefix={"Rp"} />
    ),
  },
  {
    name: "unpaid",
    title: "Unpaid",
    headerAlign: "right",
    renderText: unpaid => (
      <CurrencyDisplay value={unpaid} suffix=",-" prefix={"Rp"} />
    ),
  },
];
const formInvoiceDetail = [
  { label: "Client", value: "client_name" },
  { label: "Invoice Date", value: "invoice_date" },
  { label: "Type", value: "invoice_type" },
  { label: "Period", value: "period" },
];
const formInvoiceValue = [
  { label: "Fee", value: "fee" },
  { type: "taxes" },
  { divider: true },
  { label: "Total Fee", value: "total_fee" },
];
const approvalButtons = [
  { label: "Reject", value: "rejected" },
  { label: "Approve" },
];

export default DetailRoyaltyTracking;
